import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Button,
  ModalFooter,
  Flex,
  ModalBody,
  useToast,
  FormControl,
  FormLabel,
  Text,
  Input,
  Switch,
  Box,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { SubmitButton } from '../shared/form/SubmitButton';
import { useTranslation } from 'react-i18next';
import {
  CreateUserRequest,
  UserType,
  Role,
  userApi,
} from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Controller, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ReactSelectOption } from '@texas/types';
import { ErrorLabel } from '../shared/ErrorLabel';
import { TexasSelect } from '../shared/form/TexasSelect';
import {
  compareEnum,
  convertToEnum,
  getEnumValues,
} from '@texas/utils/helpers/enumHelpers';
import { defaultIconSize, Icons } from '../shared/Icons';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import {
  internalRole,
  userTypeFriendlyName,
} from '@texas/utils/helpers/roleHelpers';
import { userEvents } from './events';
import { RoleSelector } from './role/RoleSelector';
import { RoleRestrict } from '../shared/RoleRestrict';
import { getUserTypeOption } from './shared';
import { timezones } from '@texas/resources/countries/timezones';
import { CustomerAssignmentField } from './CustomerAssignmentField';
import { SupplierAssignmentField } from './SupplierAssignmentField';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';

export function CreateUser({
  buttonVariant = 'texas-solid',
  buttonHeight,
}: {
  buttonHeight?: string | number;
  buttonVariant?: string;
} = {}) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { t } = useTranslation();
  return (
    <RoleRestrict allow={[Role.SystemAdministrator]}>
      <Button
        h={buttonHeight}
        leftIcon={<Icons.Plus boxSize={defaultIconSize} />}
        variant={buttonVariant}
        onClick={onOpen}
      >
        {t('user.createUser')}
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('user.createUser')}</ModalHeader>
          <ModalCloseButton />
          <InnerModal onClose={onClose} />
        </ModalContent>
      </Modal>
    </RoleRestrict>
  );
}

function InnerModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    branchesData: [branches, getBranches, loadingBranches],
  } = useMetadata();

  const { request: createRequest, error } = useApiRequest(userApi.createUser);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting, isValid },
    reset,
    setValue,
    watch,
  } = useForm<CreateUserRequest>();

  useEffect(() => {
    getBranches();
  }, [getBranches]);

  const role = watch('role');
  const passwordLoginEnabled = watch('passwordLoginEnabled');
  const rudholmIdentityLoginEnabled = watch('rudholmIdentityLoginEnabled');

  useEffect(() => {
    const selectedRole = convertToEnum(Role, role);
    setValue(
      'passwordLoginEnabled',
      selectedRole !== Role.InternalAdministrator,
    );
    setValue(
      'rudholmIdentityLoginEnabled',
      selectedRole === Role.InternalAdministrator,
    );
  }, [role, setValue]);

  const onSubmit = async (data: CreateUserRequest) =>
    await request(createRequest, [data], (user) => {
      toast({
        title: t('general.created'),
        status: 'success',
        isClosable: true,
      });
      if (user.welcomeEmailFailed)
        toast({
          title: t('user.welcomeEmailFailed'),
          status: 'warning',
          isClosable: true,
          duration: null,
        });
      reset();
      onClose();
      userEvents.newUserCreated.dispatch();
    });

  return (
    <>
      {error && <ErrorDetails error={error} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody as={Flex} gap={2} flexDir="column">
          <Flex gap={2}>
            <FormControl isInvalid={!!errors.email?.message}>
              <FormLabel>{t('general.email')}</FormLabel>
              <Input
                type="email"
                {...register('email', { required: true })}
                variant="texas"
              />
              <ErrorLabel text={errors.email?.message} />
            </FormControl>
            <FormControl isInvalid={!!errors.name?.message}>
              <FormLabel>{t('general.name')}</FormLabel>
              <Input
                {...register('name', { required: true })}
                variant="texas"
              />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
          </Flex>
          <Box>
            <FormControl pb={2}>
              <FormLabel>{t('general.timeZone')}</FormLabel>
              <Controller
                name="timeZone"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    value={timezones
                      .map((x) => ({
                        label: `${x.name} ${x.utc}`,
                        value: x.zone,
                      }))
                      .find((x) => x.value === field.value)}
                    onChange={(value) => field.onChange(value?.value ?? null)}
                    isClearable={true}
                    options={timezones.map<ReactSelectOption>((u) => ({
                      label: `${u.name} ${u.utc}`,
                      value: u.zone,
                    }))}
                  />
                )}
              />
            </FormControl>
            <Text fontSize="md" fontWeight="bold">
              {t('user.sendWelcomeEmail')}
            </Text>
            <Text fontSize="sm" color="gray.200" _light={{ color: 'gray.600' }}>
              {t('user.sendWelcomeMailDesc')}
            </Text>
            <FormControl display="flex" alignItems="center" pt={2} gap={2}>
              <Controller
                name="sendWelcomeMail"
                control={control}
                render={({ field }) => (
                  <Switch
                    isChecked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              <FormLabel mb="0">{t('user.sendWelcomeMailToggle')}</FormLabel>
            </FormControl>
          </Box>
        </ModalBody>
        <ModalBody bg="texas.bg.950" py={4} my={4} _light={{ bg: 'gray.10' }}>
          <FormControl>
            <FormLabel pb={4}>{t('user.whatKindOfUserCreating')}</FormLabel>
            <Controller
              control={control}
              rules={{ required: true }}
              name="role"
              render={({ field }) => <RoleSelector {...field} />}
            />
            <ErrorLabel text={errors.role?.message} />
          </FormControl>
        </ModalBody>
        <ModalBody mb={4}>
          <Flex gap={2} flexDir="column">
            {internalRole(convertToEnum(Role, role)) && (
              <>
                <Flex gap={2}>
                  <Icons.AlertCircle boxSize={defaultIconSize} />
                  <Flex flexDir="column" gap={1}>
                    <Text fontWeight="bold">
                      {t('user.createInternalInfo')}
                    </Text>
                    <Text fontSize="sm">
                      {t('user.internalAdditionalInfo')}
                    </Text>
                  </Flex>
                </Flex>
                <Grid templateColumns="1fr 1fr" columnGap={2}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>{t('user.belongsToBranch')}</FormLabel>
                      <Controller
                        name="branchId"
                        control={control}
                        render={({ field }) => (
                          <TexasSelect
                            {...field}
                            value={branches
                              .map((x) => ({ label: x.name, value: x.id }))
                              .find((x) => x.value === field.value)}
                            onChange={(value) =>
                              field.onChange(value?.value ?? null)
                            }
                            isClearable={true}
                            isLoading={loadingBranches}
                            options={branches.map<ReactSelectOption>((u) => ({
                              label: u.name,
                              value: u.id,
                            }))}
                          />
                        )}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    {convertToEnum(Role, role) ===
                      Role.InternalAdministrator && (
                      <FormControl>
                        <FormLabel>{t('user.userType.userType')}</FormLabel>
                        <Controller
                          name="userType"
                          control={control}
                          render={({ field }) => (
                            <TexasSelect
                              {...field}
                              value={getUserTypeOption(field.value, t)}
                              onChange={(value) =>
                                field.onChange(value?.value ?? null)
                              }
                              isClearable={true}
                              options={getEnumValues(
                                UserType,
                              ).map<ReactSelectOption>((x) => ({
                                label: userTypeFriendlyName(x as UserType, t),
                                value: x,
                              }))}
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  </GridItem>
                </Grid>
                <Box>
                  <Text fontSize="md" fontWeight="bold">
                    {t('user.loginMethods')}
                  </Text>
                  <Text
                    fontSize="sm"
                    color="gray.200"
                    _light={{ color: 'gray.600' }}
                  >
                    {t('user.loginMethodsDescription')}
                  </Text>
                  <Flex gap={6} pt={2}>
                    <FormControl display="flex" alignItems="center" gap={2}>
                      <Controller
                        name="passwordLoginEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            isChecked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <FormLabel mb="0">{t('user.passwordLogin')}</FormLabel>
                    </FormControl>
                    <FormControl display="flex" alignItems="center" gap={2}>
                      <Controller
                        name="rudholmIdentityLoginEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            isChecked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <FormLabel mb="0">
                        {t('user.rudholmIdentityLogin')}
                      </FormLabel>
                    </FormControl>
                  </Flex>
                  {!passwordLoginEnabled && !rudholmIdentityLoginEnabled && (
                    <Text color="red.300" fontSize="sm" pt={2}>
                      {t('user.loginMethodRequired')}
                    </Text>
                  )}
                </Box>
                <Box
                  borderTop="1px solid"
                  borderColor="whiteAlpha.200"
                  pt={2}
                  _light={{ borderColor: 'blackAlpha.100' }}
                >
                  <Text fontSize="md" fontWeight="bold">
                    {t('userDetails.preferences')}
                  </Text>
                  <Text
                    fontSize="sm"
                    color="gray.200"
                    _light={{ color: 'gray.600' }}
                  >
                    {t('user.preferencesDescription')}
                  </Text>
                  <Flex gap={6} pt={2}>
                    <FormControl display="flex" alignItems="center" gap={2}>
                      <Controller
                        name="emailNotifications"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            isChecked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <FormLabel mb="0">
                        {t('user.emailNotifications')}
                      </FormLabel>
                    </FormControl>
                    <FormControl display="flex" alignItems="center" gap={2}>
                      <Controller
                        name="betaFeatures"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            isChecked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <FormLabel mb="0">{t('user.enableBeta')}</FormLabel>
                    </FormControl>
                  </Flex>
                </Box>
              </>
            )}
            {compareEnum(Role, role, Role.Brand) && (
              <CustomerAssignmentField control={control} />
            )}
            {compareEnum(Role, role, Role.Supplier) && (
              <SupplierAssignmentField control={control} />
            )}
          </Flex>
        </ModalBody>
        <ModalFooter
          bg="texas.bg.950"
          borderBottomRadius="md"
          as={Flex}
          gap={6}
          alignItems="end"
          pb={7}
          _light={{ bg: 'gray.50' }}
        >
          <FormControl
            opacity={internalRole(convertToEnum(Role, role)) ? 1 : 0}
            transition="opacity 100ms linear"
          >
            <FormLabel>
              <Flex gap={2}>
                <Icons.ShieldKey boxSize={defaultIconSize} />
                <Text>{t('user.passwordRequiredCreate')}</Text>
              </Flex>
            </FormLabel>
            <Input
              type="password"
              placeholder={t('user.enterYourPassword')}
              {...register('providedPassword')}
              variant="texas"
            />
          </FormControl>
          <SubmitButton
            disabled={
              !isValid ||
              (!passwordLoginEnabled && !rudholmIdentityLoginEnabled)
            }
            loading={isSubmitting}
          >
            {t('user.createUser')}
          </SubmitButton>
        </ModalFooter>
      </form>
    </>
  );
}
